import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Button,
  Chip,
  Divider,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  TextField,
} from '@mui/material';
import EuroIcon from '@mui/icons-material/Euro';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import FlagIcon from '@mui/icons-material/Flag';
import BlockIcon from '@mui/icons-material/Block';
import axios from 'axios';
import { C } from '../theme';
import { useAuth } from '../contexts/AuthContext';
import { countries, getCitiesByCountry } from '../data/locations';
import SEO from '../components/SEO';
import LocationMap from '../components/LocationMap';

interface BusinessAd {
  id: number;
  title: string;
  description: string | null; // Can be null for limited access
  price: number;
  business_type: string;
  country_id: string;
  city_id: string;
  annual_revenue?: number | null;
  employee_count?: number | null;
  established_year?: number | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
  author_name: string;
  author_id: number;
  author_email?: string; // Can be null for limited access
  images: Array<{
    id: number;
    filename: string;
    thumbnail_filename: string;
    original_filename: string;
    order_index: number;
  }>;
  created_at: string;
}

const businessTypeLabels: Record<string, string> = {
  'restaurant_hospitality': 'Restoran i ugostiteljstvo',
  'retail_services': 'Trgovina i usluge',
  'manufacturing': 'Proizvodnja',
  'technology_it': 'Tehnologija i IT',
  'healthcare': 'Zdravstvo',
  'education': 'Obrazovanje',
  'transport_logistics': 'Transport i logistika',
  'real_estate': 'Nekretnine',
  'finance_banking': 'Finansije i bankarstvo',
  'other': 'Ostalo',
};

const BusinessDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [businessAd, setBusinessAd] = useState<BusinessAd | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportLoading, setReportLoading] = useState(false);
  const [reportSuccess, setReportSuccess] = useState('');
  const [blockLoading, setBlockLoading] = useState(false);

  useEffect(() => {
    if (id) {
      fetchBusinessAd();
    }
  }, [id]);

  const fetchBusinessAd = async () => {
    try {
      setLoading(true);
      
      // Get token from localStorage
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`/api/business-ads/${id}`, { headers });
      setBusinessAd(response.data);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('Oglas za biznis nije pronađen');
      } else {
        setError('Greška pri učitavanju oglasa');
      }
      console.error('Error fetching business ad:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number) => {
    if (price >= 1000000) {
      return `€${(price / 1000000).toFixed(1)}M`;
    } else if (price >= 1000) {
      return `€${(price / 1000).toFixed(0)}K`;
    }
    return `€${price.toLocaleString()}`;
  };

  const formatRevenue = (revenue?: number) => {
    if (!revenue) return 'Nije navedeno';
    if (revenue >= 1000000) {
      return `€${(revenue / 1000000).toFixed(1)}M`;
    } else if (revenue >= 1000) {
      return `€${(revenue / 1000).toFixed(0)}K`;
    }
    return `€${revenue.toLocaleString()}`;
  };

  const getBusinessTypeLabel = (type: string) => {
    return businessTypeLabels[type] || type;
  };

  const getLocationName = () => {
    if (!businessAd) return '';
    const country = countries.find(c => c.id === Number(businessAd.country_id));
    if (!country) return '';
    
    const cities = getCitiesByCountry(Number(businessAd.country_id));
    const city = cities.find(c => c.id === Number(businessAd.city_id));
    
    return city ? `${city.name}, ${country.name}` : country.name;
  };

  const handleContact = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    // Navigate to messages with business ad info to create conversation
    // businessAd is guaranteed to be non-null here due to the render guard above
    navigate(`/messages?adId=${businessAd!.id}&receiverId=${businessAd!.author_id}&sendInitial=1`);
  };

  const handleImageClick = (imagePath: string) => {
    setSelectedImage(imagePath);
    setImageDialogOpen(true);
  };

  const closeImageDialog = () => {
    setImageDialogOpen(false);
    setSelectedImage(null);
  };

  const handleReportClick = () => {
    setReportDialogOpen(true);
    setReportReason('');
    setReportSuccess('');
    setError('');
  };

  const handleReportSubmit = async () => {
    if (!reportReason.trim()) {
      setError('Molimo unesite razlog prijave');
      return;
    }

    setReportLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        '/api/moderation/report',
        {
          content_type: 'business_ad',
          content_id: businessAd?.id,
          reported_user_id: businessAd?.author_id,
          reason: reportReason,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setReportSuccess('Biznis oglas je uspešno prijavljen. Naš tim će pregledati prijavu u roku od 24 sata.');
      setReportReason('');
      setTimeout(() => {
        setReportDialogOpen(false);
        setReportSuccess('');
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Greška pri prijavljivanju oglasa');
    } finally {
      setReportLoading(false);
    }
  };

  const handleBlockUser = async () => {
    if (!window.confirm('Da li ste sigurni da želite da blokirate ovog korisnika? Više nećete videti njihove oglase i poruke.')) {
      return;
    }

    setBlockLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        '/api/moderation/block',
        {
          blocked_id: businessAd?.author_id,
          reason: 'Blokiran putem biznis oglasa',
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      alert('Korisnik je uspešno blokiran. Više nećete videti njihov sadržaj.');
      navigate('/business');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Greška pri blokiranju korisnika');
    } finally {
      setBlockLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ pt: '90px', pb: 8, backgroundColor: C.bg, minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !businessAd) {
    return (
      <Box sx={{ pt: '90px', pb: 8, backgroundColor: C.bg, minHeight: '100vh' }}>
        <Container maxWidth="md">
          <Alert severity="error" sx={{ mt: 4 }}>
            {error || 'Greška pri učitavanju oglasa'}
          </Alert>
          <Button
            variant="outlined"
            onClick={() => navigate('/business')}
            startIcon={<ArrowBackIcon />}
            sx={{ mt: 2 }}
          >
            Nazad na biznis oglase
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: '90px', pb: 8, backgroundColor: C.bg, minHeight: '100vh' }}>
      <SEO
        title={businessAd.title}
        description={`${businessTypeLabels[businessAd.business_type] || 'Biznis'} • ${formatPrice(businessAd.price)} • Biznis na prodaju / za investiciju na platformi Nađi Investitora. ${(businessAd.description || '').replace(/\s+/g, ' ').trim().slice(0, 110)}`.slice(0, 300)}
        url={`/business/${businessAd.id}`}
        image={businessAd.images?.[0] ? `/uploads/${businessAd.images[0].filename}` : undefined}
        type="article"
      />
      <Container maxWidth="lg">
        {/* Back Button */}
        <Button
          variant="outlined"
          onClick={() => navigate('/business')}
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 3 }}
        >
          Nazad na biznis oglase
        </Button>

        <Grid container spacing={4}>
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, backgroundColor: C.surface, borderRadius: 3, border: `1px solid ${C.line}` }}>
              {/* Header */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Chip 
                    label={getBusinessTypeLabel(businessAd.business_type)}
                    color="primary"
                    sx={{ fontWeight: 600 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {new Date(businessAd.created_at).toLocaleDateString('sr-RS')}
                  </Typography>
                </Box>
                
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: C.white }}>
                  {businessAd.title}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LocationOnIcon sx={{ color: 'text.secondary' }} />
                  <Typography variant="body1" color="text.secondary">
                    {getLocationName()}
                  </Typography>
                </Box>
              </Box>

              {/* Price */}
              <Box sx={{ mb: 4, p: 3, backgroundColor: C.surface2, borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', textAlign: 'center' }}>
                  {formatPrice(businessAd.price)}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Cena biznisa
                </Typography>
              </Box>

              {/* Business Details */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: C.white }}>
                  Detalji o biznisu
                </Typography>
                
                <Grid container spacing={3}>
                  {businessAd.annual_revenue && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: C.surface2, borderRadius: 2 }}>
                        <TrendingUpIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          {formatRevenue(businessAd.annual_revenue)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Godišnji prihod
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  
                  {businessAd.employee_count && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: C.surface2, borderRadius: 2 }}>
                        <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          {businessAd.employee_count}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Broj zaposlenih
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  
                  {businessAd.established_year && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: C.surface2, borderRadius: 2 }}>
                        <CalendarTodayIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          {businessAd.established_year}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Godina osnivanja
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  
                  {/* Show message if no business details are available */}
                  {!businessAd.annual_revenue && !businessAd.employee_count && !businessAd.established_year && (
                    <Grid item xs={12}>
                      <Box sx={{ 
                        p: 3, 
                        backgroundColor: C.surface2, 
                        borderRadius: 2, 
                        textAlign: 'center',
                        border: '2px dashed #dee2e6'
                      }}>
                        <Typography variant="body2" color="text.secondary">
                          Dodatne informacije o biznisu nisu dostupne
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Description */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: C.white }}>
                  Opis biznisa
                </Typography>
                {businessAd.description ? (
                  <Typography variant="body1" sx={{ lineHeight: 1.8, color: C.muted }}>
                    {businessAd.description}
                  </Typography>
                ) : (
                  <Box sx={{
                    p: 3,
                    backgroundColor: C.surface2,
                    borderRadius: 2,
                    textAlign: 'center',
                    border: `2px dashed ${C.line}`
                  }}>
                    <LockIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                      Opis je dostupan samo investitorima
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Da biste videli detaljan opis ovog biznisa, potrebno je da se prijavite kao investitor.
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Location — backend returns coordinates only to the owner or investors */}
              {businessAd.latitude != null && businessAd.longitude != null && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: C.white }}>
                    Lokacija
                  </Typography>
                  <LocationMap lat={Number(businessAd.latitude)} lng={Number(businessAd.longitude)} height={320} />
                </Box>
              )}

              {/* Images */}
              {businessAd.images && businessAd.images.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: C.white }}>
                    Slike biznisa
                  </Typography>
                  <ImageList sx={{ width: '100%', height: 450 }} cols={3} rowHeight={164}>
                    {businessAd.images.map((image, index) => (
                      <ImageListItem key={image.id}>
                        <img
                          src={`/uploads/thumbnails/${image.thumbnail_filename}`}
                          alt={`${businessAd.title} - slika ${index + 1}`}
                          loading="lazy"
                          style={{ height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                          onClick={() => handleImageClick(`/uploads/${image.filename}`)}
                        />
                        <ImageListItemBar
                          actionIcon={
                            <IconButton
                              sx={{ color: 'white' }}
                              onClick={() => handleImageClick(`/uploads/${image.filename}`)}
                            >
                              <FullscreenIcon />
                            </IconButton>
                          }
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, backgroundColor: C.surface, borderRadius: 3, border: `1px solid ${C.line}` }}>
              {/* Contact Section */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: C.white }}>
                  Kontakt informacije
                </Typography>
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: C.muted, mb: 1 }}>
                    Vlasnik biznisa:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: C.white }}>
                    {businessAd.author_name}
                  </Typography>
                </Box>

                {user && user.userType === 'investor' ? (
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleContact}
                    startIcon={<BusinessIcon />}
                    sx={{
                      filter: 'brightness(1.06)',
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: '#333333',
                      },
                    }}
                  >
                    Kontaktiraj vlasnika
                  </Button>
                ) : (
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: C.surface2, 
                    borderRadius: 2, 
                    textAlign: 'center',
                    border: '2px dashed #dee2e6'
                  }}>
                    <LockIcon sx={{ fontSize: 24, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Kontakt opcija je dostupna samo investitorima
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Edit Button for Ad Owner */}
              {user && user.id === businessAd.author_id && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box sx={{ textAlign: 'center' }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      size="large"
                      onClick={() => navigate(`/edit-business-ad/${businessAd.id}`)}
                      startIcon={<EditIcon />}
                      sx={{
                        borderColor: 'rgba(243,192,21,0.55)',
                        borderWidth: '1.5px',
                        color: '#F6CB4B',
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: 600,
                        '&:hover': {
                          background: C.goldGrad,
                          color: C.onGold,
                          borderColor: 'rgba(243,192,21,0.55)',
                        },
                      }}
                    >
                      Uredi oglas
                    </Button>
                  </Box>
                </>
              )}

              {/* Report and Block Buttons for Non-Owners */}
              {user && user.id !== businessAd.author_id && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      size="large"
                      onClick={handleReportClick}
                      startIcon={<FlagIcon />}
                      disabled={reportLoading}
                      sx={{
                        borderColor: 'rgba(243,192,21,0.55)',
                        color: '#F6CB4B',
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: 600,
                        '&:hover': {
                          background: C.goldGrad,
                          color: C.onGold,
                          borderColor: 'rgba(243,192,21,0.55)',
                        },
                      }}
                    >
                      Prijavi
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      size="large"
                      onClick={handleBlockUser}
                      startIcon={<BlockIcon />}
                      disabled={blockLoading}
                      sx={{
                        borderColor: C.danger,
                        color: C.danger,
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: C.danger,
                          color: C.onGold,
                          borderColor: C.danger,
                        },
                      }}
                    >
                      Blokiraj
                    </Button>
                  </Box>
                </>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Business Type */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: C.muted, mb: 2 }}>
                  Kategorija:
                </Typography>
                <Chip 
                  label={getBusinessTypeLabel(businessAd.business_type)}
                  sx={{
                    fontWeight: 700,
                    backgroundColor: C.goldSoft,
                    color: '#F6CB4B',
                    border: '1px solid rgba(243,192,21,0.45)',
                  }}
                />
              </Box>

              {/* Location */}
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: C.muted, mb: 2 }}>
                  Lokacija:
                </Typography>
                <Typography variant="body1" sx={{ color: C.white }}>
                  {getLocationName()}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Image Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={closeImageDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={closeImageDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.5)',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.7)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Full size"
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeImageDialog} color="primary">
            Zatvori
          </Button>
        </DialogActions>
      </Dialog>

      {/* Report Dialog */}
      <Dialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Prijavi biznis oglas</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {reportSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {reportSuccess}
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Razlog prijave"
            value={reportReason}
            onChange={(e) => setReportReason(e.target.value)}
            placeholder="Opišite zašto prijavljujete ovaj oglas..."
            disabled={reportLoading || !!reportSuccess}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)} disabled={reportLoading}>
            Otkaži
          </Button>
          <Button
            onClick={handleReportSubmit}
            variant="contained"
            disabled={reportLoading || !!reportSuccess}
            sx={{
              background: C.goldGrad,
              color: C.onGold,
              '&:hover': {
                filter: 'brightness(1.06)',
              },
            }}
          >
            {reportLoading ? 'Prijavljivanje...' : 'Prijavi'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BusinessDetails;
